import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API, BACKEND_URL } from '../App';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  Package, ChevronLeft, Loader2, Clock, CheckCircle, 
  Truck, FileText, DollarSign, RefreshCw, Download, Eye, X, Receipt,
  User, Mail, Phone, MapPin, CreditCard, Calendar, MessageSquare, Printer, Building2, FileSpreadsheet,
  MapPinned, Send, Plus
} from 'lucide-react';

// Sortable table header component
const SortableHeader = ({ field, sortField, sortDirection, onSort, children, className = "" }) => (
  <th 
    className={`px-4 py-4 text-left text-xs font-medium text-white/50 uppercase tracking-wider cursor-pointer hover:text-white/80 transition-colors ${className}`}
    onClick={() => onSort(field)}
  >
    <div className="flex items-center gap-1">
      {children}
      {sortField === field && (
        <span className="text-[#CCFF00]">
          {sortDirection === 'asc' ? '↑' : '↓'}
        </span>
      )}
    </div>
  </th>
);

const AdminOrders = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [filesDialogOpen, setFilesDialogOpen] = useState(false);
  const [orderFiles, setOrderFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [companyFilter, setCompanyFilter] = useState(null);
  const [companyInfo, setCompanyInfo] = useState(null);
  
  // Xero invoice reference
  const [xeroInvoiceNumber, setXeroInvoiceNumber] = useState('');
  const [xeroInvoiceDate, setXeroInvoiceDate] = useState('');
  const [savingXero, setSavingXero] = useState(false);
  
  // Resend invoice state
  const [resendingInvoice, setResendingInvoice] = useState(null); // Tracks order_id being resent
  const [resendDialogOpen, setResendDialogOpen] = useState(false);
  const [resendOrderId, setResendOrderId] = useState(null);
  const [sendToAdmin, setSendToAdmin] = useState(true); // Default to checked

  const statuses = ['pending', 'confirmed', 'printing', 'post-processing', 'ready_for_collection', 'collected', 'shipped', 'delivered', 'cancelled', 'archived'];
  
  // Sorting state
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [showArchived, setShowArchived] = useState(false);

  const fetchOrders = async () => {
    try {
      const companyId = searchParams.get('company');
      let params = new URLSearchParams();
      if (filter !== 'all') params.append('status', filter);
      if (companyId) params.append('company', companyId);
      
      const queryString = params.toString() ? `?${params.toString()}` : '';
      const response = await axios.get(`${API}/orders${queryString}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      setOrders(response.data.orders || []);
      
      // If filtering by company, fetch company info
      if (companyId) {
        setCompanyFilter(companyId);
        try {
          const companyRes = await axios.get(`${API}/admin/companies/${companyId}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
          });
          setCompanyInfo(companyRes.data);
        } catch (e) {
          console.error('Failed to fetch company info');
        }
      } else {
        setCompanyFilter(null);
        setCompanyInfo(null);
      }
    } catch (error) {
      toast.error('Failed to fetch orders');
    }
    setLoading(false);
  };

  const clearCompanyFilter = () => {
    setSearchParams({});
    setCompanyFilter(null);
    setCompanyInfo(null);
  };

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, searchParams]);

  // Handle URL parameter to auto-open a specific order
  useEffect(() => {
    const highlightId = searchParams.get('highlight');
    if (highlightId && orders.length > 0) {
      const orderToOpen = orders.find(o => o.order_id === highlightId);
      if (orderToOpen) {
        viewOrder(orderToOpen);
        // Clear highlight but keep company filter
        const company = searchParams.get('company');
        if (company) {
          setSearchParams({ company });
        } else {
          setSearchParams({});
        }
      }
    }
  }, [orders]);

  const updateStatus = async (orderId, newStatus) => {
    try {
      await axios.put(`${API}/orders/${orderId}/status`, 
        { status: newStatus },
        { headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }}
      );
      toast.success('Status updated');
      fetchOrders();
      // Update selected order if viewing
      if (selectedOrder?.order_id === orderId) {
        setSelectedOrder(prev => ({ ...prev, order_status: newStatus }));
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const togglePayment = async (orderId, currentStatus) => {
    const newPaidStatus = currentStatus !== 'paid';
    try {
      await axios.put(`${API}/orders/${orderId}/payment`, 
        { paid: newPaidStatus, payment_method: newPaidStatus ? 'cash' : undefined },
        { headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }}
      );
      toast.success(newPaidStatus ? 'Marked as paid' : 'Marked as unpaid');
      fetchOrders();
      // Update selected order if viewing
      if (selectedOrder?.order_id === orderId) {
        setSelectedOrder(prev => ({ ...prev, payment_status: newPaidStatus ? 'paid' : 'pending' }));
      }
    } catch (error) {
      toast.error('Failed to update payment status');
    }
  };

  const downloadReceipt = async (orderId) => {
    try {
      toast.info('Generating receipt...');
      const response = await axios.get(`${API}/orders/${orderId}/receipt`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Receipt_${orderId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Receipt downloaded!');
    } catch (error) {
      toast.error('Failed to generate receipt');
    }
  };

  const viewOrder = (order) => {
    setSelectedOrder(order);
    setXeroInvoiceNumber(order.xero_invoice_number || '');
    setXeroInvoiceDate(order.xero_invoice_date || '');
    setViewDialogOpen(true);
  };

  const saveXeroReference = async () => {
    if (!selectedOrder) return;
    setSavingXero(true);
    try {
      const response = await axios.put(`${API}/orders/${selectedOrder.order_id}/xero-reference`, {
        xero_invoice_number: xeroInvoiceNumber,
        xero_invoice_date: xeroInvoiceDate
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      
      // Update local state
      setSelectedOrder(prev => ({
        ...prev,
        xero_invoice_number: xeroInvoiceNumber,
        xero_invoice_date: xeroInvoiceDate,
        order_status: response.data.order_status,
        payment_status: response.data.payment_status
      }));
      // Update in orders list
      setOrders(prev => prev.map(o => 
        o.order_id === selectedOrder.order_id 
          ? { ...o, xero_invoice_number: xeroInvoiceNumber, xero_invoice_date: xeroInvoiceDate, order_status: response.data.order_status, payment_status: response.data.payment_status }
          : o
      ));
      // Show confirmation based on what changed
      if (response.data.auto_confirmed && response.data.payment_status_updated) {
        toast.success('Xero reference saved, order confirmed & invoice marked as sent');
      } else if (response.data.auto_confirmed) {
        toast.success('Xero reference saved & order confirmed');
      } else if (response.data.payment_status_updated) {
        toast.success('Xero reference saved & invoice marked as sent');
      } else {
        toast.success('Xero reference saved');
      }
    } catch (error) {
      toast.error('Failed to save Xero reference');
    }
    setSavingXero(false);
  };

  const uploadXeroInvoice = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Only PDF files are allowed');
      return;
    }
    
    setSavingXero(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      await axios.post(`${API}/orders/${selectedOrder.order_id}/xero-invoice`, formData, {
        headers: { 
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('Xero invoice uploaded');
      // Update local state
      setSelectedOrder(prev => ({
        ...prev,
        xero_invoice_file: { filename: file.name },
        order_status: prev.order_status === 'pending' ? 'confirmed' : prev.order_status
      }));
      // Update orders list
      setOrders(prev => prev.map(o => 
        o.order_id === selectedOrder.order_id 
          ? { ...o, xero_invoice_file: { filename: file.name }, order_status: o.order_status === 'pending' ? 'confirmed' : o.order_status }
          : o
      ));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload invoice');
    }
    setSavingXero(false);
    // Reset file input
    e.target.value = '';
  };

  const viewXeroInvoice = () => {
    window.open(`${API}/orders/${selectedOrder.order_id}/xero-invoice?token=${localStorage.getItem('auth_token')}`, '_blank');
  };

  const deleteXeroInvoice = async () => {
    if (!window.confirm('Delete the uploaded Xero invoice?')) return;
    
    try {
      await axios.delete(`${API}/orders/${selectedOrder.order_id}/xero-invoice`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      toast.success('Invoice deleted');
      setSelectedOrder(prev => ({ ...prev, xero_invoice_file: null }));
      setOrders(prev => prev.map(o => 
        o.order_id === selectedOrder.order_id 
          ? { ...o, xero_invoice_file: null }
          : o
      ));
    } catch (error) {
      toast.error('Failed to delete invoice');
    }
  };

  const viewOrderFiles = async (order) => {
    setSelectedOrder(order);
    setLoadingFiles(true);
    setFilesDialogOpen(true);
    
    try {
      const response = await axios.get(`${API}/admin/orders/${order.order_id}/files`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      setOrderFiles(response.data.files || []);
    } catch (error) {
      toast.error('Failed to fetch order files');
      setOrderFiles([]);
    }
    setLoadingFiles(false);
  };

  const downloadFile = async (fileId, fileName) => {
    try {
      const response = await axios.get(`${API}/admin/orders/${selectedOrder.order_id}/files/${fileId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download file');
    }
  };

  const downloadAllFiles = async () => {
    // If we already have the files loaded from viewOrderFiles, download them directly
    if (orderFiles.length === 0) {
      toast.error('No files to download');
      return;
    }
    
    try {
      toast.info('Preparing files...');
      
      // If only one file, download it directly
      if (orderFiles.length === 1) {
        await downloadFile(orderFiles[0].id, orderFiles[0].name);
        return;
      }
      
      // For multiple files, download each and create a zip client-side
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();
      
      let downloadedCount = 0;
      for (const file of orderFiles) {
        try {
          const response = await axios.get(`${API}/admin/orders/${selectedOrder.order_id}/files/${file.id}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
            responseType: 'blob'
          });
          zip.file(file.name, response.data);
          downloadedCount++;
        } catch (err) {
          console.error(`Failed to download ${file.name}:`, err);
        }
      }
      
      if (downloadedCount === 0) {
        toast.error('Failed to download any files');
        return;
      }
      
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = window.URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Order_${selectedOrder.order_id}_files.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${downloadedCount} files!`);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download files');
    }
  };

  const printOrder = async (orderId) => {
    try {
      toast.info('Generating Order PDF...');
      const response = await axios.get(`${API}/orders/${orderId}/print`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Order_${orderId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Order PDF downloaded!');
    } catch (error) {
      toast.error('Failed to generate Order PDF');
    }
  };

  // Open resend invoice dialog
  const openResendDialog = (orderId, e) => {
    if (e) e.stopPropagation();
    setResendOrderId(orderId);
    setSendToAdmin(true); // Reset to default checked
    setResendDialogOpen(true);
  };

  const resendInvoice = async () => {
    if (!resendOrderId) return;
    
    setResendingInvoice(resendOrderId);
    try {
      const response = await axios.post(`${API}/admin/orders/${resendOrderId}/resend-invoice`, 
        { send_to_admin: sendToAdmin },
        { headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` } }
      );
      toast.success(response.data.message || 'Invoice email sent successfully!');
      setResendDialogOpen(false);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to resend invoice';
      toast.error(errorMsg);
    } finally {
      setResendingInvoice(null);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'confirmed': return <CheckCircle className="w-4 h-4" />;
      case 'printing': return <Package className="w-4 h-4" />;
      case 'ready_for_collection': return <MapPinned className="w-4 h-4" />;
      case 'collected': return <CheckCircle className="w-4 h-4" />;
      case 'shipped': return <Truck className="w-4 h-4" />;
      case 'delivered': return <CheckCircle className="w-4 h-4" />;
      case 'archived': return <FileSpreadsheet className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20';
      case 'confirmed': return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
      case 'printing': return 'bg-purple-500/10 text-purple-500 border-purple-500/20';
      case 'ready_for_collection': return 'bg-orange-500/10 text-orange-500 border-orange-500/20';
      case 'collected': return 'bg-teal-500/10 text-teal-500 border-teal-500/20';
      case 'shipped': return 'bg-[#CCFF00]/10 text-[#CCFF00] border-[#CCFF00]/20';
      case 'delivered': return 'bg-green-500/10 text-green-500 border-green-500/20';
      case 'archived': return 'bg-gray-500/10 text-gray-400 border-gray-500/20';
      case 'cancelled': return 'bg-red-500/10 text-red-500 border-red-500/20';
      default: return 'bg-white/10 text-white/70 border-white/20';
    }
  };

  // Sort and filter orders
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortedOrders = [...orders]
    .filter(order => showArchived || order.order_status !== 'archived')
    .sort((a, b) => {
      let aVal, bVal;
      
      switch (sortField) {
        case 'customer_name':
          aVal = a.customer_name?.toLowerCase() || '';
          bVal = b.customer_name?.toLowerCase() || '';
          break;
        case 'items':
          aVal = a.items?.length || 0;
          bVal = b.items?.length || 0;
          break;
        case 'total':
          aVal = a.total || 0;
          bVal = b.total || 0;
          break;
        case 'payment_status':
          aVal = a.payment_status || '';
          bVal = b.payment_status || '';
          break;
        case 'order_status':
          aVal = a.order_status || '';
          bVal = b.order_status || '';
          break;
        case 'created_at':
        default:
          aVal = new Date(a.created_at || 0).getTime();
          bVal = new Date(b.created_at || 0).getTime();
          break;
      }
      
      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />
      
      <main className="pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
            <div className="flex items-center gap-4">
              <Link to="/dashboard">
                <Button variant="outline" size="icon" className="border-white/20 text-white hover:bg-white/5 rounded-none">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
              </Link>
              <div>
                <span className="overline text-[#CCFF00]">Order Management</span>
                <h1 className="text-2xl font-['Chivo'] font-black text-white">Orders</h1>
              </div>
            </div>
            <div className="flex items-center gap-3 mt-4 md:mt-0">
              <Link to="/admin/create-order">
                <Button className="bg-[#CCFF00] text-black hover:bg-[#B3E600] rounded-none" data-testid="create-order-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Order
                </Button>
              </Link>
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="w-[180px] bg-white/5 border-white/10 text-white">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent className="bg-[#111] border-white/10">
                  <SelectItem value="all" className="text-white">All Orders</SelectItem>
                  {statuses.filter(s => s !== 'archived').map(s => (
                    <SelectItem key={s} value={s} className="text-white capitalize">{s.replace('-', ' ').replace('_', ' ')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button 
                variant={showArchived ? "default" : "outline"}
                className={showArchived 
                  ? "bg-gray-600 text-white hover:bg-gray-700 rounded-none" 
                  : "border-white/20 text-white/50 hover:bg-white/5 rounded-none"
                }
                onClick={() => setShowArchived(!showArchived)}
                title={showArchived ? "Hide Archived" : "Show Archived"}
                data-testid="toggle-archived-btn"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                {showArchived ? "Hide Archived" : "Show Archived"}
              </Button>
              <Button 
                variant="outline" 
                className="border-white/20 text-white hover:bg-white/5 rounded-none"
                onClick={fetchOrders}
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Company Filter Banner */}
          {companyFilter && companyInfo && (
            <div className="bg-purple-500/10 border border-purple-500/30 rounded-sm p-4 mb-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Building2 className="w-6 h-6 text-purple-500" />
                  <div>
                    <h3 className="text-purple-400 font-bold">Filtering by Company: {companyInfo.name}</h3>
                    <p className="text-white/50 text-sm">
                      Showing {orders.length} order(s) from {companyInfo.members?.length || 0} company member(s)
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Link to={`/admin/quote-requests?company=${companyFilter}`}>
                    <Button size="sm" variant="outline" className="border-purple-500/30 text-purple-400 hover:bg-purple-500/10 rounded-none">
                      <MessageSquare className="w-4 h-4 mr-2" />
                      View Quotes
                    </Button>
                  </Link>
                  <Link to={`/admin/companies`}>
                    <Button size="sm" variant="outline" className="border-purple-500/30 text-purple-400 hover:bg-purple-500/10 rounded-none">
                      <Building2 className="w-4 h-4 mr-2" />
                      Back to Companies
                    </Button>
                  </Link>
                  <Button size="sm" variant="ghost" onClick={clearCompanyFilter} className="text-white/50 hover:text-white">
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Orders Table */}
          <div className="bg-[#111] border border-white/10 rounded-sm">
            {loading ? (
              <div className="p-12 text-center">
                <Loader2 className="w-8 h-8 text-[#CCFF00] mx-auto animate-spin" />
              </div>
            ) : sortedOrders.length === 0 ? (
              <div className="p-12 text-center">
                <Package className="w-12 h-12 text-white/20 mx-auto mb-4" />
                <p className="text-white/50">{showArchived ? "No orders found" : "No orders found (archived orders hidden)"}</p>
                {!showArchived && orders.some(o => o.order_status === 'archived') && (
                  <Button 
                    variant="link" 
                    className="text-[#CCFF00] mt-2"
                    onClick={() => setShowArchived(true)}
                  >
                    Show archived orders
                  </Button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-white/5 border-b border-white/10">
                    <tr>
                      <SortableHeader field="created_at" sortField={sortField} sortDirection={sortDirection} onSort={handleSort}>Order</SortableHeader>
                      <SortableHeader field="customer_name" sortField={sortField} sortDirection={sortDirection} onSort={handleSort}>Customer</SortableHeader>
                      <SortableHeader field="items" sortField={sortField} sortDirection={sortDirection} onSort={handleSort}>Items</SortableHeader>
                      <SortableHeader field="total" sortField={sortField} sortDirection={sortDirection} onSort={handleSort}>Total</SortableHeader>
                      <SortableHeader field="payment_status" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} className="text-center">Paid</SortableHeader>
                      <SortableHeader field="order_status" sortField={sortField} sortDirection={sortDirection} onSort={handleSort}>Status</SortableHeader>
                      <th className="px-4 py-4 text-left text-xs font-medium text-white/50 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/10">
                    {sortedOrders.map((order) => (
                      <tr key={order.order_id} className={`hover:bg-white/5 cursor-pointer ${order.order_status === 'archived' ? 'opacity-60' : ''}`} onClick={() => viewOrder(order)}>
                        <td className="px-4 py-4">
                          <p className="text-white font-mono font-bold text-sm">{order.order_id}</p>
                          <p className="text-white/30 text-xs">
                            {new Date(order.created_at).toLocaleDateString('en-AU')}
                          </p>
                        </td>
                        <td className="px-4 py-4">
                          <p className="text-white text-sm">{order.customer_name}</p>
                          <p className="text-white/50 text-xs">{order.customer_email}</p>
                        </td>
                        <td className="px-4 py-4">
                          <p className="text-white text-sm">{order.items?.length || 0} item(s)</p>
                        </td>
                        <td className="px-4 py-4">
                          <p className="text-[#CCFF00] font-mono font-bold">${order.total?.toFixed(2)}</p>
                          <p className="text-white/30 text-xs">{order.payment_method?.replace('_', ' ')}</p>
                          {order.xero_invoice_number && (
                            <p className="text-purple-400 text-xs font-mono mt-1">
                              Xero: {order.xero_invoice_number}
                            </p>
                          )}
                        </td>
                        <td className="px-4 py-4 text-center" onClick={(e) => e.stopPropagation()}>
                          <div className="flex flex-col items-center gap-1">
                            <Switch
                              checked={order.payment_status === 'paid'}
                              onCheckedChange={() => togglePayment(order.order_id, order.payment_status)}
                              className="data-[state=checked]:bg-green-500"
                            />
                            <span className={`text-xs ${order.payment_status === 'paid' ? 'text-green-400' : 'text-yellow-400'}`}>
                              {order.payment_status === 'paid' ? 'Paid' : 'Unpaid'}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                          <Badge variant="outline" className={`capitalize ${getStatusColor(order.order_status)}`}>
                            <span className="mr-1">{getStatusIcon(order.order_status)}</span>
                            {order.order_status?.replace('-', ' ')}
                          </Badge>
                        </td>
                        <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-blue-400 hover:text-blue-400 hover:bg-blue-400/10 h-8 w-8 p-0"
                              onClick={() => viewOrder(order)}
                              title="View Order"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-[#CCFF00] hover:text-[#CCFF00] hover:bg-[#CCFF00]/10 h-8 w-8 p-0"
                              onClick={() => viewOrderFiles(order)}
                              title="Download Files"
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-purple-400 hover:text-purple-400 hover:bg-purple-400/10 h-8 w-8 p-0"
                              onClick={() => downloadReceipt(order.order_id)}
                              title="Download Receipt"
                            >
                              <Receipt className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-cyan-400 hover:text-cyan-400 hover:bg-cyan-400/10 h-8 w-8 p-0"
                              onClick={(e) => openResendDialog(order.order_id, e)}
                              disabled={resendingInvoice === order.order_id}
                              title="Resend Invoice Email"
                              data-testid={`resend-invoice-btn-${order.order_id}`}
                            >
                              {resendingInvoice === order.order_id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Send className="w-4 h-4" />
                              )}
                            </Button>
                            {order.payment_status !== 'paid' && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="text-green-400 hover:text-green-400 hover:bg-green-400/10 h-8 w-8 p-0"
                                onClick={(e) => { e.stopPropagation(); togglePayment(order.order_id, order.payment_status); }}
                                title="Mark as Paid"
                                data-testid={`mark-paid-btn-${order.order_id}`}
                              >
                                <DollarSign className="w-4 h-4" />
                              </Button>
                            )}
                            <Select 
                              value={order.order_status} 
                              onValueChange={(v) => updateStatus(order.order_id, v)}
                            >
                              <SelectTrigger className="bg-white/5 border-white/10 text-white w-32 h-8 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-[#111] border-white/10">
                                {statuses.map(s => (
                                  <SelectItem key={s} value={s} className="text-white capitalize text-sm">{s.replace('-', ' ')}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* View Order Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="bg-[#111] border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Package className="w-5 h-5 text-[#CCFF00]" />
                Order {selectedOrder?.order_id}
              </div>
              <Badge variant="outline" className={`capitalize ${getStatusColor(selectedOrder?.order_status)}`}>
                {selectedOrder?.order_status?.replace('-', ' ')}
              </Badge>
            </DialogTitle>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="space-y-6 py-4">
              {/* Customer Info */}
              <div className="bg-white/5 rounded-sm p-4">
                <h3 className="text-white font-bold mb-3 flex items-center gap-2">
                  <User className="w-4 h-4 text-[#CCFF00]" />
                  Customer Information
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-white/50">Name</p>
                    <p className="text-white">{selectedOrder.customer_name}</p>
                  </div>
                  <div>
                    <p className="text-white/50">Email</p>
                    <p className="text-white">{selectedOrder.customer_email}</p>
                  </div>
                  {selectedOrder.customer_phone && (
                    <div>
                      <p className="text-white/50">Phone</p>
                      <p className="text-white">{selectedOrder.customer_phone}</p>
                    </div>
                  )}
                  {selectedOrder.contact_person && (
                    <div>
                      <p className="text-white/50">Contact Person</p>
                      <p className="text-white font-medium">{selectedOrder.contact_person}</p>
                    </div>
                  )}
                  {selectedOrder.company_name && (
                    <div>
                      <p className="text-white/50">Company</p>
                      <p className="text-[#CCFF00] font-medium">{selectedOrder.company_name}</p>
                    </div>
                  )}
                  {selectedOrder.company_abn && (
                    <div>
                      <p className="text-white/50">ABN</p>
                      <p className="text-white font-mono">{selectedOrder.company_abn}</p>
                    </div>
                  )}
                  {selectedOrder.po_number && (
                    <div>
                      <p className="text-white/50">PO Number</p>
                      <p className="text-[#CCFF00] font-mono font-medium">{selectedOrder.po_number}</p>
                    </div>
                  )}
                  {selectedOrder.customer_address && (
                    <div className="col-span-2">
                      <p className="text-white/50">Address</p>
                      <p className="text-white">{selectedOrder.customer_address}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Order Items */}
              <div className="bg-white/5 rounded-sm p-4">
                <h3 className="text-white font-bold mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-[#CCFF00]" />
                  Order Items ({selectedOrder.items?.length || 0})
                </h3>
                <div className="space-y-3">
                  {selectedOrder.items?.map((item, idx) => {
                    const unitPrice = item.unit_price || (item.total_price / Math.max(item.quantity || 1, 1));
                    return (
                      <div key={idx} className="flex justify-between items-center py-2 border-b border-white/10 last:border-0">
                        <div>
                          <p className="text-white font-medium">{item.file_name}</p>
                          <p className="text-white/50 text-sm">
                            {item.material} • {item.volume_cm3?.toFixed(2)} cm³ • Qty: {item.quantity}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-white/50 text-xs">
                            ${unitPrice?.toFixed(2)} × {item.quantity}
                          </p>
                          <p className="text-[#CCFF00] font-mono">${item.total_price?.toFixed(2)}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Payment Info */}
              <div className="bg-white/5 rounded-sm p-4">
                <h3 className="text-white font-bold mb-3 flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-[#CCFF00]" />
                  Payment Summary
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-white/50">Subtotal</span>
                    <span className="text-white">${selectedOrder.subtotal?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/50">Delivery</span>
                    <span className="text-white">${selectedOrder.delivery_cost?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-white/10">
                    <span className="text-white font-bold">Total</span>
                    <span className="text-[#CCFF00] font-bold font-mono">${selectedOrder.total?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between pt-2">
                    <span className="text-white/50">Payment Method</span>
                    <span className="text-white capitalize">{selectedOrder.payment_method?.replace('_', ' ')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/50">Payment Status</span>
                    <Badge className={selectedOrder.payment_status === 'paid' ? 'bg-green-500/10 text-green-400' : 'bg-yellow-500/10 text-yellow-400'}>
                      {selectedOrder.payment_status}
                    </Badge>
                  </div>
                </div>
              </div>

              {/* Xero Invoice Reference - for all payment methods */}
              <div className="bg-purple-500/10 border border-purple-500/20 rounded-sm p-4">
                <div className="flex items-center gap-2 mb-3">
                  <FileSpreadsheet className="w-5 h-5 text-purple-400" />
                  <h4 className="text-purple-400 font-bold">Xero Invoice Reference</h4>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white/70 text-sm">Invoice Number</Label>
                    <Input
                      value={xeroInvoiceNumber}
                      onChange={(e) => setXeroInvoiceNumber(e.target.value)}
                      placeholder="e.g., INV-0001"
                      className="bg-white/5 border-white/10 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-white/70 text-sm">Invoice Date</Label>
                    <Input
                      type="date"
                      value={xeroInvoiceDate}
                      onChange={(e) => setXeroInvoiceDate(e.target.value)}
                      className="bg-white/5 border-white/10 text-white mt-1"
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between mt-3">
                  <p className="text-white/30 text-xs">
                    {selectedOrder.payment_method === 'invoice' && ['pending', 'pending_invoice'].includes(selectedOrder.order_status)
                      ? 'Saving will auto-confirm the order and update payment status'
                      : 'Link this order to a Xero invoice for accounting'}
                  </p>
                  <Button
                    size="sm"
                    onClick={saveXeroReference}
                    disabled={savingXero}
                    className="bg-purple-500 text-white hover:bg-purple-600 rounded-none"
                  >
                    {savingXero ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Reference'}
                  </Button>
                </div>
                
                {/* Invoice File Upload Section */}
                <div className="mt-4 pt-4 border-t border-purple-500/20">
                  <Label className="text-white/70 text-sm">Upload Xero Invoice PDF</Label>
                  <div className="flex items-center gap-2 mt-2">
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={uploadXeroInvoice}
                      className="hidden"
                      id="xero-invoice-upload"
                    />
                    <label
                      htmlFor="xero-invoice-upload"
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-white/5 border border-dashed border-white/20 rounded-sm cursor-pointer hover:bg-white/10 transition-colors"
                    >
                      <Download className="w-4 h-4 text-white/50 rotate-180" />
                      <span className="text-white/50 text-sm">
                        {selectedOrder.xero_invoice_file ? 'Replace invoice' : 'Choose PDF file'}
                      </span>
                    </label>
                  </div>
                  
                  {/* Show uploaded file */}
                  {selectedOrder.xero_invoice_file && (
                    <div className="mt-3 flex items-center justify-between bg-white/5 p-3 rounded-sm">
                      <div className="flex items-center gap-2">
                        <FileText className="w-5 h-5 text-purple-400" />
                        <span className="text-white text-sm">{selectedOrder.xero_invoice_file.filename}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={viewXeroInvoice}
                          className="text-purple-400 hover:text-purple-400 hover:bg-purple-400/10 h-8 px-2"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={deleteXeroInvoice}
                          className="text-red-400 hover:text-red-400 hover:bg-red-400/10 h-8 w-8 p-0"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
                
                {selectedOrder.xero_invoice_number && (
                  <div className="mt-3 pt-3 border-t border-purple-500/20 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    <span className="text-green-400 text-sm">
                      Linked to Xero: <strong>{selectedOrder.xero_invoice_number}</strong>
                      {selectedOrder.xero_invoice_date && ` (${new Date(selectedOrder.xero_invoice_date).toLocaleDateString('en-AU')})`}
                    </span>
                  </div>
                )}
              </div>

              {/* Order Meta */}
              <div className="flex items-center gap-4 text-sm text-white/50">
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  Created: {new Date(selectedOrder.created_at).toLocaleString('en-AU')}
                </div>
              </div>

              {/* Linked Quote Request Reference */}
              {selectedOrder.from_quote_request && selectedOrder.quote_request_id && (
                <div className="bg-blue-500/10 border border-blue-500/20 rounded-sm p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-blue-400 text-sm font-medium">From Quote Request</p>
                      <p className="text-white font-mono font-bold">{selectedOrder.quote_request_id}</p>
                    </div>
                    <Link to={`/admin/quote-requests?highlight=${selectedOrder.quote_request_id}`}>
                      <Button
                        size="sm"
                        className="bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 rounded-none"
                      >
                        <MessageSquare className="w-4 h-4 mr-2" />
                        View Request
                      </Button>
                    </Link>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-wrap gap-3">
                <Button 
                  variant="outline" 
                  className="flex-1 min-w-[120px] border-white/20 text-white"
                  onClick={() => { setViewDialogOpen(false); viewOrderFiles(selectedOrder); }}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Files
                </Button>
                <Button 
                  variant="outline"
                  className="flex-1 min-w-[120px] border-white/20 text-white"
                  onClick={() => downloadReceipt(selectedOrder.order_id)}
                >
                  <Receipt className="w-4 h-4 mr-2" />
                  Receipt
                </Button>
                <Button 
                  variant="outline"
                  className="flex-1 min-w-[120px] border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10"
                  onClick={(e) => openResendDialog(selectedOrder.order_id, e)}
                  disabled={resendingInvoice === selectedOrder.order_id}
                  data-testid="resend-invoice-dialog-btn"
                >
                  {resendingInvoice === selectedOrder.order_id ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Resend Invoice
                </Button>
                {selectedOrder.payment_status !== 'paid' && (
                  <Button 
                    variant="outline"
                    className="flex-1 min-w-[120px] border-green-500/50 text-green-400 hover:bg-green-500/10"
                    onClick={() => togglePayment(selectedOrder.order_id, selectedOrder.payment_status)}
                    data-testid="mark-paid-dialog-btn"
                  >
                    <DollarSign className="w-4 h-4 mr-2" />
                    Mark as Paid
                  </Button>
                )}
                <Button 
                  className="flex-1 min-w-[120px] bg-[#CCFF00] text-black hover:bg-[#B3E600]"
                  onClick={() => printOrder(selectedOrder.order_id)}
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Print Order
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Files Dialog */}
      <Dialog open={filesDialogOpen} onOpenChange={setFilesDialogOpen}>
        <DialogContent className="bg-[#111] border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Download className="w-5 h-5 text-[#CCFF00]" />
              Order Files - {selectedOrder?.order_id}
            </DialogTitle>
          </DialogHeader>
          
          {loadingFiles ? (
            <div className="py-8 text-center">
              <Loader2 className="w-6 h-6 text-[#CCFF00] mx-auto animate-spin" />
            </div>
          ) : orderFiles.length === 0 ? (
            <div className="py-8 text-center">
              <FileText className="w-12 h-12 text-white/20 mx-auto mb-4" />
              <p className="text-white/50">No files found</p>
            </div>
          ) : (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                {orderFiles.map((file, idx) => (
                  <div 
                    key={idx}
                    className="flex items-center justify-between p-3 bg-white/5 rounded-sm hover:bg-white/10"
                  >
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-[#CCFF00]" />
                      <span className="text-white text-sm truncate max-w-[200px]">{file.name}</span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-[#CCFF00] hover:text-[#CCFF00] hover:bg-[#CCFF00]/10"
                      onClick={() => downloadFile(file.id, file.name)}
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
              
              <Button 
                className="w-full bg-[#CCFF00] text-black hover:bg-[#B3E600]"
                onClick={downloadAllFiles}
              >
                <Download className="w-4 h-4 mr-2" />
                Download All as ZIP
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Resend Invoice Dialog */}
      <Dialog open={resendDialogOpen} onOpenChange={setResendDialogOpen}>
        <DialogContent className="bg-[#1A1A1A] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-cyan-400" />
              Resend Invoice
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <p className="text-white/70 text-sm">
              Send the invoice email to the customer for order <span className="text-[#CCFF00] font-medium">{resendOrderId}</span>
            </p>
            
            <div className="flex items-start gap-3 p-3 bg-white/5 border border-white/10 rounded-lg">
              <Checkbox 
                id="send-to-admin"
                checked={sendToAdmin}
                onCheckedChange={setSendToAdmin}
                className="mt-0.5 border-cyan-500 data-[state=checked]:bg-cyan-500 data-[state=checked]:border-cyan-500"
                data-testid="send-to-admin-checkbox"
              />
              <div className="flex-1">
                <Label 
                  htmlFor="send-to-admin" 
                  className="text-white cursor-pointer font-medium"
                >
                  Send copy to Admin
                </Label>
                <p className="text-white/50 text-xs mt-1">
                  Receive a copy at hello@3dprintbureau.com.au for your records. 
                  Useful for filing in Outlook or forwarding to other contacts.
                </p>
              </div>
            </div>
          </div>
          
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              className="flex-1 border-white/20 text-white hover:bg-white/10"
              onClick={() => setResendDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button 
              className="flex-1 bg-cyan-500 text-white hover:bg-cyan-600"
              onClick={resendInvoice}
              disabled={resendingInvoice === resendOrderId}
              data-testid="confirm-resend-btn"
            >
              {resendingInvoice === resendOrderId ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Invoice
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default AdminOrders;
